/*
 * Questions - the front page's frequent questions (S51, 2026-11-04).
 *
 * The owner's request: a question is a sentence and a reference to the article
 * that answers it; up to eight are supported; the front page lists them under
 * "Frequent Questions" as links, each only for readers who may read its article.
 *
 * The page is the promoted shortlist's page with a sentence on every row
 * (PromotedArticlesPage.tsx), and keeps its rules: it edits a LOCAL copy and
 * saves the whole list at once, matching the API's replace-all, so a reorder is
 * instant and nothing is half-saved; it reorders with up and down buttons,
 * which work for a keyboard as well as a pointer; and a question whose article
 * readers cannot reach stays on the list, marked, rather than vanishing from
 * the page that is supposed to show what is stored.
 *
 * Each row's sentence is edited in place, and so is its article (the owner,
 * after TEST, 2026-11-04: "It should be possible to change the target article
 * without having to delete the question and recreate it again"): "Change
 * article" opens a search under the row, and choosing an article there points
 * the question at it - same sentence, same place. One such search is open at a
 * time; Escape or Cancel closes it (asked the same day). Two questions may lead
 * to the same article. Every article search on the page suggests five.
 *
 * Its words come from the chosen language's dictionary, `t.admin.site` (S48).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { AdminArticleListItem, AdminFrequentQuestion } from '@accotool-kb/shared';
import { FREQUENT_QUESTION_MAX_LENGTH, MAX_FREQUENT_QUESTIONS } from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { reorderList } from './reorderList.js';
import { useAction, useAsync, useDebounced } from '../hooks/useAsync.js';
import { ErrorBanner, Loading, Tag } from '../components/ui.js';
import { useMessages } from '../i18n/language.js';

/** One question in the working copy. `key` is the row's identity while it is edited. */
interface Row {
  key: number;
  question: string;
  articleId: string;
  article: AdminFrequentQuestion['article'];
}

let rowSequence = 0;

function toRows(items: readonly AdminFrequentQuestion[]): Row[] {
  return items.map((item) => ({ key: ++rowSequence, question: item.question, articleId: item.articleId, article: item.article }));
}

/** What a row says about its article, from a picker's row - the shape the server answers. */
function toTarget(article: AdminArticleListItem): AdminFrequentQuestion['article'] {
  return {
    title: article.title ?? article.slug,
    slug: article.slug,
    isLive: article.status === 'live',
    isPrivate: article.visibility === 'private',
    product: article.product,
  };
}

/** Whether the working copy, as it would be saved, is what is stored. */
function matchesStored(rows: readonly Row[], stored: readonly AdminFrequentQuestion[]): boolean {
  return (
    rows.length === stored.length &&
    rows.every((row, index) => row.question.trim() === stored[index]?.question && row.articleId === stored[index]?.articleId)
  );
}

export function FrequentQuestionsPage(): JSX.Element {
  const s = useMessages().admin.site.questions;
  const api = useAdminApi();
  const state = useAsync(() => api.listQuestions(), []);

  // The working copy. Seeded from the load, then owned by the page until Save.
  const [rows, setRows] = useState<Row[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Read out by the aria-live region: each reorder and each change of article,
  // which the page otherwise shows silently.
  const [announcement, setAnnouncement] = useState('');
  // The row whose article is being changed, if any - one search open at a time.
  const [changing, setChanging] = useState<number | null>(null);
  // Where focus goes when that search closes: back to the row's own button.
  const changeButtons = useRef(new Map<number, HTMLButtonElement>());
  const [refocus, setRefocus] = useState<number | null>(null);
  const save = useAction();

  useEffect(() => {
    if (state.data && rows === null) setRows(toRows(state.data));
  }, [state.data, rows]);

  useEffect(() => {
    if (refocus === null) return;
    changeButtons.current.get(refocus)?.focus();
    setRefocus(null);
  }, [refocus]);

  const dirty = useMemo(() => !!state.data && !!rows && !matchesStored(rows, state.data), [rows, state.data]);
  // An empty sentence is not saved as anything: the row is written or removed.
  const hasBlank = rows?.some((row) => row.question.trim() === '') ?? false;

  const move = (from: number, delta: -1 | 1) => {
    setRows((current) => {
      if (!current) return current;
      const moving = current[from];
      if (!moving) return current;
      const to = from + delta;
      const next = reorderList(current, from, to);
      if (next === current) {
        setAnnouncement(delta < 0 ? s.alreadyFirst(moving.question) : s.alreadyLast(moving.question));
        return current;
      }
      setAnnouncement(s.moved(moving.question, to + 1, next.length));
      return [...next];
    });
  };

  const edit = (key: number, question: string) => {
    setRows((current) => current?.map((row) => (row.key === key ? { ...row, question } : row)) ?? current);
  };

  const remove = (key: number) => {
    setRows((current) => current?.filter((row) => row.key !== key) ?? current);
    if (changing === key) setChanging(null);
  };

  const closeChange = (key: number) => {
    setChanging(null);
    setRefocus(key);
  };

  const retarget = (key: number, position: number, article: AdminArticleListItem) => {
    setRows(
      (current) =>
        current?.map((row) => (row.key === key ? { ...row, articleId: article.articleId, article: toTarget(article) } : row)) ??
        current,
    );
    setAnnouncement(s.changed(position, article.title ?? article.slug));
    closeChange(key);
  };

  const add = (question: string, article: AdminArticleListItem) => {
    setRows((current) => {
      if (!current || current.length >= MAX_FREQUENT_QUESTIONS) return current;
      return [...current, { key: ++rowSequence, question, articleId: article.articleId, article: toTarget(article) }];
    });
  };

  const onSave = async () => {
    if (!rows || hasBlank) return;
    const stored = await save.run(() =>
      api.setQuestions(rows.map((row) => ({ question: row.question.trim(), articleId: row.articleId }))),
    );
    if (stored) {
      // The PUT answers the stored list; asking again would only add a window in
      // which a successful save could render as an error.
      setRows(toRows(stored));
      setChanging(null);
      state.setData(stored);
      setNotice(s.saved);
    }
  };

  if (state.loading && !state.data) return <Loading label={s.loading} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!rows) return <Loading />;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{s.title}</h1>
          <p>{s.intro(MAX_FREQUENT_QUESTIONS)}</p>
        </div>
        <button
          type="button"
          className="button button-primary"
          disabled={!dirty || hasBlank || save.running}
          onClick={() => void onSave()}
        >
          {save.running ? s.saving : s.save}
        </button>
      </div>

      <p className="visually-hidden" aria-live="polite">
        {announcement}
      </p>

      {save.error && <ErrorBanner error={save.error} onRetry={() => void onSave()} />}
      {notice && !dirty && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}

      <div className="card">
        {rows.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            {s.empty}
          </p>
        ) : (
          <ol className="promoted-admin-list question-admin-list">
            {rows.map((row, index) => {
              const position = index + 1;
              const inputId = `question-${row.key}`;
              const changeId = `question-${row.key}-change`;
              const blank = row.question.trim() === '';
              const open = changing === row.key;
              return (
                <li key={row.key} className="promoted-admin-row question-admin-row">
                  <span className="promoted-admin-position">{position}</span>
                  <div className="question-admin-body">
                    <label htmlFor={inputId} className="visually-hidden">
                      {s.questionLabel(position)}
                    </label>
                    <input
                      id={inputId}
                      type="text"
                      value={row.question}
                      maxLength={FREQUENT_QUESTION_MAX_LENGTH}
                      onChange={(event) => edit(row.key, event.target.value)}
                      aria-invalid={blank || undefined}
                      {...(blank ? { 'aria-describedby': `${inputId}-error` } : {})}
                    />
                    {blank && (
                      <p id={`${inputId}-error`} className="field-error">
                        {s.blank}
                      </p>
                    )}
                    {/*
                      Which article, and whether readers can follow the link: the
                      product tag tells two "Getting started"s apart, as on the
                      promoted page, and the two marks say who will not see it.
                      Then the way to point the question somewhere else.
                    */}
                    <div className="question-admin-target">
                      <span className="faint">{s.linksTo}</span>{' '}
                      <span className="question-admin-article">{row.article.title}</span>
                      <span className="tag-list">
                        <Tag kind="product">{row.article.product.name}</Tag>
                        {!row.article.isLive && <Tag kind="neutral">{s.notShown}</Tag>}
                        {row.article.isLive && row.article.isPrivate && <Tag kind="audience">{s.privateArticle}</Tag>}
                      </span>
                      <button
                        type="button"
                        className="button button-small button-quiet"
                        ref={(element) => {
                          if (element) changeButtons.current.set(row.key, element);
                          else changeButtons.current.delete(row.key);
                        }}
                        aria-label={s.changeArticleFor(position)}
                        aria-expanded={open}
                        aria-controls={open ? changeId : undefined}
                        onClick={() => (open ? closeChange(row.key) : setChanging(row.key))}
                        onKeyDown={(event) => {
                          if (open && event.key === 'Escape') {
                            event.preventDefault();
                            closeChange(row.key);
                          }
                        }}
                      >
                        {s.changeArticle}
                      </button>
                    </div>
                    {open && (
                      /*
                        Escape anywhere inside the search cancels it (the owner,
                        2026-11-04: "at least hitting the esc key should cancel
                        it"), and so does the Cancel button, which sits beside the
                        search box rather than under the results, where it could
                        not be seen.
                      */
                      <div
                        id={changeId}
                        className="question-admin-change"
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            closeChange(row.key);
                          }
                        }}
                      >
                        <ArticleSearch
                          inputId={`${changeId}-search`}
                          label={s.changeSearchLabel(position)}
                          autoFocus
                          excludeArticleId={row.articleId}
                          actionText={s.choose}
                          actionLabel={(title) => s.chooseFor(position, title)}
                          onChoose={(article) => retarget(row.key, position, article)}
                          beside={
                            <button
                              type="button"
                              className="button"
                              title={s.cancelHint}
                              aria-keyshortcuts="Escape"
                              onClick={() => closeChange(row.key)}
                            >
                              {s.cancel}
                            </button>
                          }
                        />
                      </div>
                    )}
                  </div>
                  <span className="promoted-admin-actions">
                    <button
                      type="button"
                      className="button button-quiet"
                      aria-label={s.moveUp(position)}
                      onClick={() => move(index, -1)}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="button button-quiet"
                      aria-label={s.moveDown(position)}
                      onClick={() => move(index, 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className="button button-quiet"
                      aria-label={s.removeFrom(position)}
                      onClick={() => remove(row.key)}
                    >
                      {s.remove}
                    </button>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <AddQuestionPanel full={rows.length >= MAX_FREQUENT_QUESTIONS} onAdd={add} />
    </>
  );
}

/**
 * The sentence, then the article that answers it - inline rather than a dialog,
 * as on the promoted page: adding is half of this page's job. Every article is
 * offered, the ones already used included, since two questions may share one.
 */
function AddQuestionPanel({
  full,
  onAdd,
}: {
  full: boolean;
  onAdd: (question: string, article: AdminArticleListItem) => void;
}): JSX.Element {
  const s = useMessages().admin.site.questions;
  const [question, setQuestion] = useState('');
  const written = question.trim();

  return (
    <div className="card" style={{ marginTop: 'var(--space-4)' }}>
      <h2>{s.addTitle}</h2>
      {full ? (
        <p className="muted" style={{ margin: 0 }}>
          {s.full(MAX_FREQUENT_QUESTIONS)}
        </p>
      ) : (
        <>
          <div className="field">
            <label htmlFor="new-question">{s.newQuestionLabel}</label>
            <input
              id="new-question"
              type="text"
              value={question}
              maxLength={FREQUENT_QUESTION_MAX_LENGTH}
              placeholder={s.newQuestionPlaceholder}
              onChange={(event) => setQuestion(event.target.value)}
              aria-describedby="new-question-hint"
            />
            <p id="new-question-hint" className="field-hint">
              {s.newQuestionHint}
            </p>
          </div>
          <ArticleSearch
            inputId="question-article-search"
            label={s.searchLabel}
            disabled={written === ''}
            actionText={s.add}
            actionLabel={s.addWith}
            onChoose={(article) => {
              onAdd(written, article);
              setQuestion('');
            }}
          />
        </>
      )}
    </div>
  );
}

/**
 * How many articles a search suggests: the owner's five (2026-11-04), whether
 * nothing has been typed yet - the most recently modified - or something has.
 * Typing narrows it; a longer list only pushed the rest of the page away.
 */
const SUGGESTIONS = 5;

/**
 * An article search with one button per result - the add panel's, and each
 * row's when its article is being changed. The promoted picker's rows: the
 * title, a mark for an article that is not live, its categories and its product.
 * When changing, the question's own article is left out: choosing it would
 * change nothing - which is why one more than five is asked for, so leaving it
 * out still leaves five. `beside` sits next to the search box: the change
 * search's Cancel.
 */
function ArticleSearch({
  inputId,
  label,
  autoFocus = false,
  disabled = false,
  excludeArticleId,
  actionText,
  actionLabel,
  onChoose,
  beside,
}: {
  inputId: string;
  label: string;
  autoFocus?: boolean;
  disabled?: boolean;
  excludeArticleId?: string;
  actionText: string;
  actionLabel: (title: string) => string;
  onChoose: (article: AdminArticleListItem) => void;
  beside?: ReactNode;
}): JSX.Element {
  const s = useMessages().admin.site.questions;
  const api = useAdminApi();
  const [draft, setDraft] = useState('');
  const q = useDebounced(draft, 350);

  const results = useAsync(
    (signal) =>
      api.listArticles(
        { q, products: [], categories: [], status: 'all', page: 1, pageSize: SUGGESTIONS + 1, sort: 'modified' },
        signal,
      ),
    [q],
  );

  const candidates = (results.data?.items ?? [])
    .filter((article) => article.articleId !== excludeArticleId)
    .slice(0, SUGGESTIONS);

  return (
    <>
      <div className="question-search-head">
        <div className="field">
          <label htmlFor={inputId}>{label}</label>
          <input
            id={inputId}
            type="search"
            placeholder={s.searchPlaceholder}
            value={draft}
            // Only where a click has just opened this search for the row; the
            // add panel's is never focused for the reader.
            autoFocus={autoFocus}
            onChange={(event) => setDraft(event.target.value)}
          />
        </div>
        {beside}
      </div>
      {results.error && <ErrorBanner error={results.error} onRetry={results.reload} />}
      {results.loading && !results.data && <Loading label={s.searching} />}
      {results.data && (
        <ul className="promoted-admin-list" style={{ marginTop: 'var(--space-3)' }}>
          {candidates.length === 0 && <li className="muted">{s.noCandidates}</li>}
          {candidates.map((article) => {
            const title = article.title ?? article.slug;
            return (
              <li key={article.articleId} className="promoted-admin-row">
                <span className="promoted-admin-title">{title}</span>
                {article.status !== 'live' && <Tag kind="neutral">{s.status(article.status)}</Tag>}
                {/* The promoted picker's tags, in its order and for its reasons. */}
                <span className="tag-list promoted-admin-tags">
                  {article.categories.map((category) => (
                    <Tag key={`c-${category.categoryId}`} kind="category">
                      {category.name}
                    </Tag>
                  ))}
                  <Tag kind="product">{article.product.name}</Tag>
                </span>
                <span className="promoted-admin-actions">
                  <button
                    type="button"
                    className="button button-quiet"
                    disabled={disabled}
                    aria-label={actionLabel(title)}
                    onClick={() => onChoose(article)}
                  >
                    {actionText}
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
